import { environment } from '@config/environment.config';
import { authStore } from '@services/authStore.service';
import { normalizeOperation } from './catalogNormalizer';
import type { OperationTypeDefinition } from '@/graph/domain/types';

/**
 * Fetch both built-in operations (GET /api/operations) and custom operations
 * (POST /api/customOperations/get) from the backend, then normalize them into
 * the canonical OperationTypeDefinition shape.
 *
 * Mirrors the Angular client's `getOperations()` in MxgraphComponent:
 *   - GET /api/operations → built-in (subType-based)
 *   - POST /api/customOperations/get → custom (operationType-based)
 */
export async function fetchOperationCatalog(): Promise<OperationTypeDefinition[]> {
  const base = environment.getIlaApiBaseUrl();
  const headers = {
    'Content-Type': 'application/json',
    ...authStore.getAuthHeader(),
  };

  const [builtInRes, customRes] = await Promise.all([
    fetch(`${base}/api/operations`, { headers }),
    fetch(`${base}/api/customOperations/get`, { method: 'POST', headers, body: '{}' }),
  ]);

  const builtIn = builtInRes.ok ? await builtInRes.json() : [];
  const custom = customRes.ok ? await customRes.json() : [];

  const builtInArr = Array.isArray(builtIn) ? builtIn : [];
  const customArr = Array.isArray(custom) ? custom : [];

  const allRaw = [...builtInArr, ...customArr];

  // Deduplicate by id (custom ops take precedence)
  const seen = new Set<string>();
  const deduped: Record<string, unknown>[] = [];
  for (const raw of allRaw.reverse()) {
    const id = String(raw._id ?? raw.id ?? '');
    if (id && !seen.has(id)) {
      seen.add(id);
      deduped.push(raw as Record<string, unknown>);
    }
  }

  return deduped.map(normalizeOperation);
}
